import type { CompanySetupView } from '@helios/types';
import { apiClient, type ApiResult } from './apiClient';
import { getSessionOriginKey } from './sessionOrigin';

export interface SetupSummary {
  id: string;
  name: string;
  slug: string;
  ownerName: string;
  ownerEmail: string;
  status: string;
  version: number;
  invitationAccepted: boolean;
  invitationExpiresAt?: string | null;
  invitationState?: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
  deliveryState?: string;
}
export interface SetupInvitation {
  id: string;
  invitationCode: string | null;
  expiresAt: string;
  delivery: string;
  detail?: string;
  deliveryState?: 'ACCEPTED' | 'FAILED' | 'UNKNOWN';
}
export interface InvitationAuthority {
  platformOperator: boolean;
  emailAvailable: boolean;
  transport: string | null;
  sender: string | null;
  requiredSender?: string;
  deliveryFingerprint: string | null;
}
export type SetupDraft = Pick<
  CompanySetupView,
  | 'profile'
  | 'roster'
  | 'providers'
  | 'tasks'
  | 'importMode'
  | 'accessApproved'
  | 'rolePolicyRevision'
  | 'sourceApproved'
  | 'aiConsent'
>;
async function unwrap<T>(promise: Promise<ApiResult<T>>): Promise<T> {
  const result = await promise;
  if (!result.ok || result.data === null)
    throw new Error(result.error ?? 'Company setup is unavailable');
  return result.data;
}
const base = '/v1/company-setup';
const scoped = () => ({ sessionOrigin: getSessionOriginKey(), allowRefresh: false });
export const companySetupClient = {
  invitationAuthority: () =>
    unwrap(apiClient.get<InvitationAuthority>(`${base}/invitation-authority`, scoped())),
  list: (query?: { page: number; q?: string }) => {
    const params = query
      ? new URLSearchParams({ page: String(query.page), ...(query.q ? { q: query.q } : {}) })
      : null;
    return unwrap(apiClient.get<SetupSummary[]>(params ? `${base}?${params}` : base, scoped()));
  },
  get: (id: string) => unwrap(apiClient.get<CompanySetupView>(`${base}/${id}`, scoped())),
  create: (body: unknown) => unwrap(apiClient.post<SetupInvitation>(base, body, scoped())),
  accept: (body: unknown) =>
    unwrap(
      apiClient.post<{ tenantSlug: string; email: string; detail: string }>(
        `${base}/accept`,
        body,
        { allowRefresh: false },
      ),
    ),
  reissue: (
    id: string,
    expectedVersion: number,
    delivery?: 'SECURE_HANDOFF' | 'EMAIL',
    deliveryFingerprint?: string,
  ) =>
    unwrap(
      apiClient.post<SetupInvitation>(
        `${base}/${id}/reissue-owner-invitation`,
        {
          expectedVersion,
          ...(delivery ? { delivery } : {}),
          ...(deliveryFingerprint ? { deliveryFingerprint } : {}),
        },
        scoped(),
      ),
    ),
  revokeInvitation: (id: string, expectedVersion: number) =>
    unwrap(
      apiClient.post<{ revoked: true }>(
        `${base}/${id}/revoke-owner-invitation`,
        { expectedVersion },
        scoped(),
      ),
    ),
  save: (id: string, expectedVersion: number, draft: SetupDraft) =>
    unwrap(
      apiClient.put<CompanySetupView>(`${base}/${id}`, { expectedVersion, ...draft }, scoped()),
    ),
  activate: (id: string, expectedVersion: number) =>
    unwrap(
      apiClient.post<{ activated: boolean; detail: string }>(
        `${base}/${id}/activate`,
        { expectedVersion },
        scoped(),
      ),
    ),
  ask: (id: string, expectedVersion: number, message: string) =>
    unwrap(
      apiClient.post<{ state: string; reply: string }>(
        `${base}/${id}/assistant`,
        { expectedVersion, message },
        scoped(),
      ),
    ),
  decide: (id: string, proposalId: string, expectedVersion: number, decision: 'APPLY' | 'REJECT') =>
    unwrap(
      apiClient.post<CompanySetupView>(
        `${base}/${id}/proposals/${proposalId}/decision`,
        { expectedVersion, decision },
        scoped(),
      ),
    ),
  preview: (id: string, expectedVersion: number, key: string) =>
    unwrap(
      apiClient.post<Record<string, unknown>>(
        `${base}/${id}/provider-preview`,
        { expectedVersion, key },
        scoped(),
      ),
    ),
  provision: (id: string, email: string, credentialDelivery: 'DISPLAY' | 'EMAIL') =>
    unwrap(
      apiClient.post<{
        temporaryPassword: string | null;
        deliveryState: string;
        credentialEmailStatus: string;
      }>(`${base}/${id}/provision-person`, { email, credentialDelivery }, scoped()),
    ),
};
